import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MergeFieldInserter } from '@/components/messaging/merge-field-inserter';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { toast } from '@/components/ui/toast';
import { api } from '@/lib/api';
import { ApiError } from '@/lib/api-errors';
import { insertAtSelection } from '@/lib/messaging-merge-fields';

export interface MessageTemplateRow {
  id: string;
  name: string;
  category: string;
  subjectPush: string | null;
  bodyPush: string | null;
  bodySms: string | null;
  isSystem: boolean;
}

interface TemplateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  template?: MessageTemplateRow | null;
  /** Prefill when creating (e.g. from the compose form). Ignored when editing. */
  defaults?: {
    name?: string;
    subjectPush?: string | null;
    bodyPush?: string | null;
    bodySms?: string | null;
  } | null;
  onSaved?: (template: MessageTemplateRow) => void;
}

export const TemplateDialog = ({
  open,
  onOpenChange,
  template,
  defaults = null,
  onSaved,
}: TemplateDialogProps) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation();
  const { t: tErrors } = useTranslation('errors');
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [bodySms, setBodySms] = useState('');
  const bodySmsRef = useRef<HTMLTextAreaElement>(null);
  const [selection, setSelection] = useState({ start: 0, end: 0 });

  useEffect(() => {
    if (!open) return;
    if (template) {
      setName(template.name);
      setBodySms(template.bodySms || template.bodyPush || '');
      return;
    }
    setName(defaults?.name ?? '');
    setBodySms(defaults?.bodySms || defaults?.bodyPush || '');
  }, [open, template, defaults]);

  const rememberSelection = () => {
    const el = bodySmsRef.current;
    if (!el) return;
    setSelection({
      start: el.selectionStart ?? bodySms.length,
      end: el.selectionEnd ?? bodySms.length,
    });
  };

  const insertInto = (token: string) => {
    const { value: next, caret } = insertAtSelection(
      bodySms,
      selection.start,
      selection.end,
      token,
    );
    setBodySms(next);
    setSelection({ start: caret, end: caret });
    requestAnimationFrame(() => {
      bodySmsRef.current?.focus();
      bodySmsRef.current?.setSelectionRange(caret, caret);
    });
  };

  const save = useMutation({
    mutationFn: () => {
      const payload = {
        name: name.trim(),
        subjectPush: null,
        bodyPush: null,
        bodySms: bodySms.trim() || null,
      };
      if (template) {
        return api.patch<MessageTemplateRow>(`/messaging/templates/${template.id}`, payload);
      }
      return api.post<MessageTemplateRow>('/messaging/templates', payload);
    },
    onSuccess: (saved) => {
      toast.success(t('templates.saved'));
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ['messaging', 'templates'] });
      onSaved?.(saved);
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? tErrors(err.code, err.params) : tErrors('UNEXPECTED'));
    },
  });

  const canSubmit = name.trim() && bodySms.trim() && !template?.isSystem;
  const readOnly = Boolean(template?.isSystem);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="lg" closeLabel={tCommon('action.close')} className="overflow-visible">
        <DialogHeader>
          <DialogTitle>
            {template ? t('templates.edit') : t('templates.create')}
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="max-h-[75vh] pr-2">
          <div className="space-y-4">
            <div>
              <Label htmlFor="template-name">{t('templates.name')}</Label>
              <Input
                id="template-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={readOnly}
              />
            </div>
            <div>
              <Label htmlFor="template-body-sms">{t('templates.bodySms')}</Label>
              <Textarea
                ref={bodySmsRef}
                id="template-body-sms"
                value={bodySms}
                onChange={(e) => setBodySms(e.target.value)}
                onSelect={rememberSelection}
                onKeyUp={rememberSelection}
                onClick={rememberSelection}
                onBlur={rememberSelection}
                rows={5}
                disabled={readOnly}
              />
              <p className="mt-1 text-caption text-fg-secondary">{t('templates.bodySmsHelp')}</p>
              {!readOnly ? <MergeFieldInserter onInsert={insertInto} /> : null}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => onOpenChange(false)}>
                {tCommon('action.cancel')}
              </Button>
              {!readOnly ? (
                <Button
                  disabled={!canSubmit || save.isPending}
                  onClick={() => save.mutate()}
                >
                  {template ? tCommon('action.saveChanges') : t('templates.create')}
                </Button>
              ) : null}
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};
